"use client";

import React from "react";
import { Eye, BookOpen, CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { VocabularyCategory } from "@/lib/constants/vocabulary-categories";

// Selection check icon component
const CircleCheckIcon = ({ className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`lucide lucide-circle-check-icon lucide-circle-check ${className}`}
  >
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

interface VocabularyCardProps {
  category: VocabularyCategory;
  isSelected: boolean;
  onClick: (categoryId: string) => void;
  onPreview?: (categoryId: string, categoryTitle: string) => void;
  isLoading?: boolean;
}

export const VocabularyCard: React.FC<VocabularyCardProps> = ({
  category,
  isSelected,
  onClick,
  onPreview,
  isLoading = false,
}) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isLoading) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick(category.id);
    }
  };

  const handleClick = () => {
    if (isLoading) return;
    onClick(category.id);
  };

  const handlePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLoading || !onPreview) return;
    onPreview(category.id, category.title);
  };

  return (
    <Card
      role="button"
      tabIndex={isLoading ? -1 : 0}
      aria-pressed={isSelected}
      aria-selected={isSelected}
      aria-disabled={isLoading}
      aria-label={`Select ${category.title} vocabulary (${category.wordCount})`}
      className={`p-3 relative overflow-hidden transition-all duration-300 group focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded-xl border min-h-[105px] ${
        isLoading
          ? "opacity-60 cursor-not-allowed"
          : isSelected
          ? "ring-2 ring-primary/50 bg-gradient-to-br from-primary/10 to-primary/5 border-primary/30 shadow-lg cursor-pointer"
          : "hover:shadow-lg hover:scale-[1.02] hover:-translate-y-0.5 active:scale-[0.995] cursor-pointer touch-manipulation border-border/60 hover:border-primary/20"
      }`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {/* Selected badge */}
      {isSelected && (
        <div className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-medium shadow-sm">
          <CheckCircle2 className="w-3 h-3" aria-hidden="true" />
          <span>Selected</span>
        </div>
      )}
      {/* Modern book icon decoration */}
      <div className="absolute top-3 right-3 opacity-20 group-hover:opacity-30 transition-opacity duration-300 pointer-events-none">
        <BookOpen className="h-8 w-8 text-primary transform rotate-12" />
      </div>

      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-primary/20 to-transparent rounded-full transform translate-x-8 -translate-y-8"></div>
        <div className="absolute bottom-0 left-0 w-12 h-12 bg-gradient-to-tr from-primary/10 to-transparent rounded-full transform -translate-x-6 translate-y-6"></div>
      </div>

      {/* Card content */}
      <div className={`relative z-10 space-y-2 pr-10 ${isSelected ? "pt-6" : ""}` }>
        {/* Title */}
        <div
          className="text-sm font-semibold text-foreground leading-tight pr-8"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {category.title}
        </div>

        {/* Subtitle */}
        <div
          className="text-xs text-muted-foreground leading-relaxed pr-4"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {category.subtitle}
        </div>

        {/* Word count */}
        <div className="flex items-center justify-between mt-auto">
          <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md ${
            isSelected ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"
          }`}>
            <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="text-xs font-medium">{category.wordCount}</span>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div
        className="absolute top-2 right-2 flex items-center gap-1.5"
        style={{ zIndex: 10 }}
      >
        {/* Preview button */}
        {onPreview && (
          <button
            onClick={handlePreview}
            className="p-1.5 bg-background/90 hover:bg-primary/10 border border-border/60 hover:border-primary/30 rounded-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-300 hover:scale-105 shadow-sm backdrop-blur-sm active:scale-95"
            aria-label={`Preview ${category.title} vocabulary`}
            disabled={isLoading}
          >
            <Eye className="h-3 w-3 text-muted-foreground hover:text-primary transition-colors" />
          </button>
        )}

        {/* Selection indicator */}
        {isSelected && (
          <div className="animate-in zoom-in-75 duration-300 bg-primary/10 rounded-lg p-1 backdrop-blur-sm">
            <CircleCheckIcon className="h-4 w-4 text-primary drop-shadow-sm" />
          </div>
        )}
      </div>
    </Card>
  );
};
